import { ChangeDetectionStrategy, Component, Signal, computed, inject } from "@angular/core";

import { AnalyticsClickDirective, ClickAreaDirective } from "@directives";
import { GameFeatures, MenuTab, SvgName } from "@models";
import { TranslatePipe } from "@pipes";
import { ActionService, AnalyticsService, AudioService, ClientService, EMusic, ESound } from "@services";

import { ButtonComponent } from "../button/button.component";
import { SvgComponent } from "../svg/svg.component";
import { SwitcherComponent } from "../switcher/switcher.component";

interface Item {
  icon: SvgName;
  text: string;
  index: MenuTab;
}

interface Switcher {
  value: Signal<boolean>;
  text: string;
  fn: () => void;
}

@Component({
  selector: "mn-menu-tab",
  styleUrls: ["./menu-tab.component.scss"],
  templateUrl: "./menu-tab.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SvgComponent, SwitcherComponent, ButtonComponent, AnalyticsClickDirective, TranslatePipe, ClickAreaDirective],
})
export class MenuTabComponent {
  private readonly clS = inject(ClientService);
  private readonly acS = inject(ActionService);
  private readonly anS = inject(AnalyticsService);
  private readonly auS = inject(AudioService);

  public readonly items = computed<Item[]>(() => {
    const features = this.acS.gSB$()?.enabled_features || [];

    return [
      { icon: "clock", text: "bets", index: MenuTab.B },
      { icon: "question", text: "how-to-play", index: MenuTab.H_I },
      { icon: "cards", text: "rules", index: MenuTab.R_I },
      { icon: "warning", text: "limits", index: MenuTab.L },
      features.includes(GameFeatures.P_F) ? { icon: "fair", text: "provably-fair", index: MenuTab.F } : null,
    ].filter(Boolean) as Item[];
  });
  public readonly switchers: Switcher[] = [
    {
      text: "sound",
      value: computed(() => !this.clS.sF$().sound_off),
      fn: this.sound.bind(this),
    },
    {
      text: "music",
      value: computed(() => !this.clS.sF$().music_off),
      fn: this.music.bind(this),
    },
  ];

  public close(): void {
    this.acS.mS$.set(null);
  }

  public select(event: Event, index: number): void {
    event.stopPropagation();

    this.acS.mS$.set({ tab: index });
  }

  private sound(): void {
    const value = !this.clS.sF$().sound_off;

    this.clS.set("sound_off", value);

    this.anS.client(`sound_turned_${value ? "off" : "on"}`);
    if (value) {
      Object.values(ESound).forEach(it => this.auS.stop(it));
    }
  }

  private music(): void {
    const value = !this.clS.sF$().music_off;

    this.clS.set("music_off", value);

    this.anS.client(`music_turned_${value ? "off" : "on"}`);

    if (value) {
      this.auS.stop(EMusic.B);
    } else {
      this.auS.music(EMusic.B);
    }
  }
}
